import { LayerStatus, PointArray } from '@editor/interfaces/attribute'
import { isNewPointOverlapping } from '@editor/services/Points'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

export interface PlaneData extends LayerStatus {
  id: string
  points: PointArray[]
  isDoubleClickedClosed: boolean
  error?: string
}

export interface PlaneDetectionState {
  /**
   * Working points when drawing plane.
   */
  workingPoints: PointArray[]

  /**
   * Completed planes.
   */
  planes: PlaneData[]

  /**
   * Saving planes, used to store planes as they are saved to the server.
   */
  savingPlanes: PlaneData[]

  /**
   * Planes that errors after a failed save.
   */
  errorPlanes: PlaneData[]

  /**
   * Flag to indicate if the polygon is closing.
   */
  isClosing: boolean

  /**
   * Flag to indicate if the tool is dirty (has working items).
   */
  isDirty: boolean
}

const initialState: PlaneDetectionState = {
  workingPoints: [],
  planes: [],
  savingPlanes: [],
  errorPlanes: [],
  isClosing: false,
  isDirty: false,
}

export const slice = createSlice({
  name: 'editor/tools/planeDetection',
  initialState,
  reducers: {
    /**
     * Add a new working point.
     */
    addWorkingPoint: (state, { payload }: PayloadAction<PointArray>) => {
      // Filter out points that are too close to existing points
      if (!isNewPointOverlapping(state.workingPoints, payload)) {
        state.workingPoints.push(payload)
      }
      state.isDirty = true
    },

    /**
     * Add a new plane.
     */
    addPlane: (state, action: PayloadAction<PointArray[]>) => {
      state.planes.push({
        id: crypto.randomUUID(),
        points: action.payload,
        isDoubleClickedClosed: false,
      })
    },

    /**
     * Complete drawing a plane.
     */
    completePlane: (state, { payload: { isDoubleClicked } }: PayloadAction<{ isDoubleClicked: boolean }>) => {
      state.planes.push({
        id: crypto.randomUUID(),
        points: state.workingPoints,
        isDoubleClickedClosed: isDoubleClicked,
      })
      state.workingPoints = []
    },

    /**
     * Update the points of a plane.
     */
    updatePlanePoints: (state, action: PayloadAction<{ planeIndex: number; points: PointArray[] }>) => {
      const { planeIndex, points } = action.payload
      if (state.planes[planeIndex]) {
        state.planes[planeIndex].points = [...points]
      }
    },

    /**
     * Remove a completed plane.
     */
    removePlane: (state, action: PayloadAction<number>) => {
      state.planes.splice(action.payload, 1)
      state.isDirty = state.planes.length > 0 || state.workingPoints.length > 0
    },

    /**
     * Set closing state.
     */
    setIsClosing: (state, action: PayloadAction<boolean>) => {
      state.isClosing = action.payload
    },

    /**
     * Reset working points.
     */
    resetWorkingPoints: (state) => {
      state.workingPoints = []
      state.isDirty = state.planes.length > 0
    },

    /**
     * Undo the last working plane, re-opening any completed ones if necessary.
     */
    undoLastWorkingPlane: (state) => {
      if (state.workingPoints.length > 0) {
        state.workingPoints.pop()
      } else if (state.planes.length) {
        // take the last plane and make it the working plane
        const lastPlane = state.planes.pop()
        if (lastPlane) {
          // Polygons are now 'open-loop' (no duplicate start point at end), so keep all points
          state.workingPoints = lastPlane.points
        }
      }

      state.isDirty = state.planes.length > 0 || state.workingPoints.length > 0
    },

    // ## Saving planes
    copySavingPlanes: (state) => {
      state.savingPlanes.push(...state.planes)
      state.planes = []
      state.isDirty = false
    },
    clearSavedPlanes: (state, { payload }: PayloadAction<string[]>) => {
      state.savingPlanes = state.savingPlanes.filter(({ id }) => !payload.includes(id))
    },

    // ## Error planes
    /**
     * Copy saving planes to error planes.
     */
    copyErrorPlanes: (state, { payload }: PayloadAction<{ id: string; error: string }[]>) => {
      state.errorPlanes = state.savingPlanes
        .filter((plane) => payload.some(({ id }) => id === plane.id))
        .map<PlaneData>((plane) => ({
          ...plane,
          error: payload.find(({ id }) => id === plane.id)?.error,
        }))
        .concat(state.errorPlanes)
      state.savingPlanes = state.savingPlanes.filter((plane) => !payload.some(({ id }) => id === plane.id))
    },
    /**
     * Remove error planes by ID.
     */
    removeErrorPlanes: (state, { payload }: PayloadAction<string[]>) => {
      state.errorPlanes = state.errorPlanes.filter((plane) => !payload.includes(plane.id))
    },

    /**
     * Reset
     */
    reset: () => initialState,
    resetWorking: (state) => {
      state.workingPoints = []
      state.planes = []
      state.isDirty = state.planes.length > 0
      state.isClosing = false
    },
  },
})

export const {
  setIsClosing,

  // ## Working plane
  addWorkingPoint,
  resetWorkingPoints,
  undoLastWorkingPlane,

  // ## Completed planes
  addPlane,
  completePlane,
  updatePlanePoints,
  removePlane,

  // ## Saving planes
  copySavingPlanes,
  clearSavedPlanes,

  // ## Error planes
  copyErrorPlanes,
  removeErrorPlanes,

  reset,
  resetWorking,
} = slice.actions

export default slice.reducer
